import type { MaybeRef } from 'vue';
import { toPlainText } from '@portabletext/toolkit';
import { sanityImageWithAsset } from '@pkg/sanity-utils/sanityImageWithAsset';
import type { AuthorLocalised } from '@pkg/sanity-utils/types/author';
import { authorsPathSegment } from '../../../sanity/schemas/utils/slugs/slugConstants';
import { buildBreadcrumbList, resolveSiteBaseUrl } from './useJsonLdShared';

/**
 * Builds and injects JSON-LD for blog author pages:
 * CollectionPage + Person|Organization + BreadcrumbList (Home > Author).
 * Author URLs are `/{locale}/author/{slug}` (not under /blog).
 */
export function useAuthorJsonLd(author: MaybeRef<AuthorLocalised | null | undefined>) {
  const runtimeConfig = useRuntimeConfig();
  const localePath = useLocalePath();
  const { t } = useI18n();
  const { $urlFor } = useNuxtApp();

  useHead({
    script: computed(() => {
      const authorData = unref(author);
      const authorSlug = authorData?.slug?.current?.trim();
      if (!authorData?.name || !authorSlug) {
        return [];
      }

      const baseUrl = resolveSiteBaseUrl(runtimeConfig.public.domain as string | undefined);
      const pageUrl = `${baseUrl}${localePath(`/${authorsPathSegment}/${authorSlug}`)}`;
      const homepageUrl = `${baseUrl}${localePath('/')}`;

      const webpageId = `${pageUrl}#webpage`;
      const authorId = `${pageUrl}#author`;
      const breadcrumbId = `${pageUrl}#breadcrumb`;
      const organizationId = `${baseUrl}/#organization`;

      const isOrganization = authorData.authorType === 'organization';
      const authorSchemaType = isOrganization ? 'Organization' : 'Person';

      const descriptionFromBio = authorData.bio
        ? toPlainText(authorData.bio).trim()
        : '';
      const description =
        descriptionFromBio ||
        authorData.seoSocial?.metaDescription?.trim() ||
        authorData.jobTitle?.trim() ||
        '';

      const authorImage = sanityImageWithAsset(authorData.image);
      const imageUrl = authorImage
        ? $urlFor(authorImage).width(512).height(512).url()
        : undefined;

      const authorEntity = {
        '@type': authorSchemaType,
        '@id': authorId,
        name: authorData.name,
        url: pageUrl,
        ...(description && { description }),
        ...(imageUrl && { image: imageUrl }),
        ...(authorData.expertise?.length && { knowsAbout: authorData.expertise }),
        ...(authorData.areaServed?.length && { areaServed: authorData.areaServed }),
        ...(!isOrganization && {
          ...(authorData.jobTitle && { jobTitle: authorData.jobTitle }),
          worksFor: { '@id': organizationId },
        }),
        ...(isOrganization && {
          ...(authorData.foundingDate && { foundingDate: authorData.foundingDate }),
          parentOrganization: { '@id': organizationId },
          memberOf: { '@id': organizationId },
        }),
      };

      return [
        {
          key: 'author-jsonld',
          type: 'application/ld+json',
          innerHTML: JSON.stringify({
            '@context': 'https://schema.org',
            '@graph': [
              {
                '@type': 'CollectionPage',
                '@id': webpageId,
                url: pageUrl,
                name: authorData.seoSocial?.metaTitle || authorData.name,
                ...(description && { description }),
                about: { '@id': authorId },
                mainEntity: { '@id': authorId },
                breadcrumb: { '@id': breadcrumbId },
              },
              authorEntity,
              buildBreadcrumbList(breadcrumbId, [
                { name: t('home'), item: homepageUrl },
                { name: authorData.name, item: pageUrl },
              ]),
            ],
          }),
        },
      ];
    }),
  });
}
